// Reacts to a 401 from any authenticated API call (a stale/invalid Tahiti
// token) by clearing the local session and sending the user back to
// /login?reason=expired. The router is injected via
// `registerSessionExpiryHandler` at boot (see main.tsx) instead of imported
// directly here, so this module — and api.ts, which calls
// `notifyUnauthorized` — never has to know about React or the route tree.

import { resetAuthGuardCache } from './auth-guard';
import { clearToken } from './auth-service';

/**
 * The subset of TanStack Router's router instance this module actually
 * uses, kept narrow so this module stays decoupled from the full route tree.
 */
export interface SessionExpiryRouter {
  readonly state: { readonly location: { readonly pathname: string; readonly href: string } };
  navigate: (opts: {
    to: '/login';
    search: { redirect?: string; reason?: 'expired' };
  }) => unknown;
}

let activeRouter: SessionExpiryRouter | null = null;
let redirecting = false;

/** Wire the real router in. Call once at boot, before the app renders (see main.tsx). */
export function registerSessionExpiryHandler(router: SessionExpiryRouter): void {
  activeRouter = router;
}

/**
 * Called by the API layer (api.ts) on any 401 — a stale/invalid Tahiti
 * token. Clears the dead token and sends the user back to
 * /login?reason=expired, preserving the current path to return to after
 * login. A no-op if a redirect is already in flight (several requests can
 * all 401 in the same tick). Token clearing happens regardless of whether
 * the router has been registered; only navigation requires the router.
 */
export function notifyUnauthorized(): void {
  if (redirecting) return;

  redirecting = true;
  clearToken();
  resetAuthGuardCache();

  if (activeRouter === null || activeRouter.state.location.pathname === '/login') return;

  void activeRouter.navigate({
    to: '/login',
    search: { redirect: activeRouter.state.location.href, reason: 'expired' },
  });
}

/** Reset the dedup flag once a fresh login succeeds (see routes/login.tsx). */
export function resetSessionExpiry(): void {
  redirecting = false;
}
